import { ButtonBase, IconButton, Typography } from '@mui/material';
import { Search } from 'lucide-react';
import type { ReactElement } from 'react';

import { Icon, Kbd } from '../kit';
import { useCommandPalette } from './paletteContext';
import { shortcutAria, shortcutLabel } from './shortcut';

/**
 * Champ de recherche de la barre superieure : il **ouvre** la palette, il ne
 * saisit rien lui-meme (un bouton en forme de champ, pas un second champ qui
 * volerait le focus a celui de la palette).
 *
 * Son libelle n'annonce que ce que la palette sait chercher : un compte, une
 * page. Sous 900 px il se reduit a un bouton d'icone, comme le reste de la barre.
 */
export const SearchTrigger = ({ compact }: { readonly compact: boolean }): ReactElement => {
  const { open } = useCommandPalette();

  if (compact) {
    return (
      <IconButton
        aria-label="Rechercher un compte ou une page"
        aria-haspopup="dialog"
        onClick={open}
        sx={{ width: 40, height: 40, border: 1, borderColor: 'tokens.border', color: 'tokens.textMuted' }}
      >
        <Icon icon={Search} size={18} />
      </IconButton>
    );
  }

  return (
    <ButtonBase
      aria-haspopup="dialog"
      aria-keyshortcuts={shortcutAria()}
      onClick={open}
      sx={{
        flex: '0 1 420px',
        minWidth: 0,
        height: 40,
        gap: 1.25,
        px: 1.5,
        justifyContent: 'flex-start',
        borderRadius: '10px',
        border: 1,
        borderColor: 'tokens.border',
        bgcolor: 'tokens.surfaceMuted',
        color: 'tokens.textMuted',
        '&:hover': { borderColor: 'tokens.textMuted' },
      }}
    >
      <Icon icon={Search} size={18} />
      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 14, textAlign: 'left', color: 'inherit' }}>
        Rechercher un compte, aller à une page…
      </Typography>
      <Kbd>{shortcutLabel()}</Kbd>
    </ButtonBase>
  );
};
